import { Image } from '@bfe/moly';
import clsx from 'clsx';
import { MouseEvent, ReactNode } from 'react';

type MediaObject = {
  name: string;
  url: string;
  icon?: ReactNode;
  className?: string;
};

export type SocialMediasProps = {
  className?: string;
  size?: number;
  medias: MediaObject[];
  buttonNamePrefix?: string;
  onClick?: (event: MouseEvent, data: MediaObject) => void;
};

export function SocialMedias({
  className,
  size = 16,
  medias = [],
  onClick,
  buttonNamePrefix = '',
}: SocialMediasProps) {
  const mediaList = medias
    .map(({ name, url, icon }) => ({
      name,
      url: url,
      icon: icon || mediaIcons[name] || mediaIcons.website,
    }))
    .filter((m) => Boolean(m.url));

  if (!mediaList.length) {
    return null;
  }

  return (
    <div className={clsx(className, 'svg-full flex')}>
      {mediaList.map((media) => {
        return (
          <a
            target="_blank"
            href={media.url}
            key={media.name}
            data-spm-even="Web3UserClick"
            data-button_name={`${buttonNamePrefix}${media.name}`}
            onClick={(e) => onClick?.(e, media)}
            className={clsx(className, 'svg-full')}
            aria-label={media.name}
            style={{
              display: 'block',
              width: `${size}px`,
              height: `${size}px`,
            }}
          >
            {typeof media.icon === 'string' ? (
              <Image src={media.icon} width={size} height={size} />
            ) : (
              media.icon || mediaIcons.website
            )}
          </a>
        );
      })}
    </div>
  );
}

// 下面都是图标配置
const mediaIcons = {
  website: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <rect width="24" height="24" rx="12" fill="#404347" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.51921 4.39209C6.62076 5.33659 4.44954 7.89135 4.06189 11H7.05857C7.28634 8.63684 8.13716 6.37308 9.51921 4.39209ZM14.4808 4.39209C15.8628 6.37308 16.7137 8.63684 16.9414 11H19.9381C19.5505 7.89135 17.3792 5.33659 14.4808 4.39209ZM14.9305 11C14.661 8.62706 13.6505 6.35811 12 4.45916C10.3495 6.35811 9.33896 8.62706 9.06954 11H14.9305ZM9.06954 13H14.9305C14.661 15.3729 13.6505 17.6419 12 19.5408C10.3495 17.6419 9.33897 15.3729 9.06954 13ZM7.05858 13H4.06189C4.44954 16.1087 6.62078 18.6634 9.51925 19.6079C8.13718 17.6269 7.28636 15.3632 7.05858 13ZM14.4807 19.6079C15.8628 17.6269 16.7136 15.3632 16.9414 13H19.9381C19.5505 16.1087 17.3792 18.6634 14.4807 19.6079ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z"
        fill="white"
      />
    </svg>
  ),
  twitter: (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="12" fill="#F8F9FB" />
      <g clipPath="url(#clip0_10965_7615)">
        <path
          d="M13.1271 10.925L18.2242 5H17.0163L12.5905 10.1446L9.05561 5H4.97852L10.324 12.7795L4.97852 18.9928H6.18644L10.8602 13.5599L14.5934 18.9928H18.6704L13.1268 10.925H13.1271ZM11.4727 12.8481L10.931 12.0734L6.62167 5.90931H8.47698L11.9547 10.8839L12.4963 11.6586L17.0169 18.1249H15.1616L11.4727 12.8484V12.8481Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_10965_7615">
          <rect
            width="13.6919"
            height="14"
            fill="white"
            transform="translate(4.97803 5)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  telegram: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle cx="12" cy="12" r="12" fill="#0088CC" />
      <path
        d="M17.9884 7.0361C18.0953 6.3457 17.4388 5.80076 16.825 6.07028L4.59835 11.4384C4.15813 11.6317 4.19033 12.2985 4.6469 12.4439L7.16834 13.2469C7.64957 13.4001 8.17067 13.3209 8.59089 13.0306L14.2756 9.10309C14.447 8.98466 14.6339 9.22839 14.4874 9.3794L10.3955 13.5983C9.99851 14.0075 10.0773 14.701 10.5548 15.0004L15.1362 17.8734C15.65 18.1956 16.3111 17.8719 16.4072 17.251L17.9884 7.0361Z"
        fill="white"
      />
    </svg>
  ),
  reddit: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <g clipPath="url(#clip0_10965_7617)">
        <path
          d="M11.9766 23.9766C18.604 23.9766 23.9766 18.604 23.9766 11.9766C23.9766 5.34914 18.604 -0.0234375 11.9766 -0.0234375C5.34914 -0.0234375 -0.0234375 5.34914 -0.0234375 11.9766C-0.0234375 18.604 5.34914 23.9766 11.9766 23.9766Z"
          fill="#FF4500"
        />
        <path
          d="M9.86955 15.7328L9.86958 15.7329C10.0422 15.9054 10.3597 16.0593 10.7842 16.1666C11.1962 16.2708 11.6491 16.3163 12.0283 16.3163C12.4073 16.3163 12.8566 16.2709 13.2667 16.1668C13.6866 16.0601 14.0078 15.9065 14.1901 15.7298C14.5119 15.4113 15.0367 15.4123 15.3573 15.7328L15.3882 15.7638L15.4133 15.7996C15.6277 16.1059 15.654 16.5784 15.3292 16.9032C14.8002 17.4321 14.0668 17.6871 13.4561 17.816C12.8345 17.9472 12.2695 17.9619 12.0143 17.9619C11.7561 17.9619 11.1882 17.9433 10.5661 17.8105C9.95595 17.6803 9.22179 17.4256 8.69934 16.9032L9.86955 15.7328ZM9.86955 15.7328C9.54796 15.4113 9.02091 15.4113 8.69934 15.7328C8.3777 16.0544 8.37769 16.5816 8.6993 16.9032L9.86955 15.7328ZM16.7473 11.1453L17.0922 11.3928L17.3924 11.0927C17.6072 10.8778 17.901 10.7478 18.2458 10.7478C18.9381 10.7478 19.5002 11.3099 19.5002 12.0022C19.5002 12.5142 19.1858 12.9643 18.7705 13.1669L18.4351 13.3305L18.4965 13.6986C18.5199 13.839 18.5318 13.9798 18.5318 14.1355C18.5318 15.274 17.8715 16.3542 16.6973 17.1706C15.5245 17.986 13.8762 18.5057 12.0283 18.5057C10.1804 18.5057 8.53208 17.986 7.35935 17.1706C6.18512 16.3542 5.52479 15.274 5.52479 14.1355C5.52479 13.9777 5.53699 13.8229 5.56008 13.6844L5.62317 13.3058L5.27378 13.147C4.8253 12.9432 4.52832 12.5118 4.52832 12.0022C4.52832 11.3099 5.09043 10.7478 5.78266 10.7478C6.10631 10.7478 6.42045 10.8869 6.64442 11.1007L6.94734 11.39L7.28518 11.1425C8.39768 10.3275 9.96567 9.80476 11.7485 9.75112L12.0858 9.74097L12.3459 9.75097C14.085 9.81786 15.6435 10.3532 16.7473 11.1453ZM16.2611 6.03005L16.2545 5.87482L16.3551 5.65919C16.4706 5.41158 16.7167 5.24609 17.0107 5.24609C17.4224 5.24609 17.7599 5.58364 17.7599 5.99522C17.7599 6.40677 17.4224 6.74434 17.0107 6.74434C16.6062 6.74434 16.2779 6.4234 16.2611 6.03005ZM9.24935 11.5022C8.28552 11.5022 7.50023 12.2875 7.50023 13.2513C7.50023 14.2152 8.28549 15.0005 9.24935 15.0005C10.2132 15.0005 10.9985 14.2152 10.9985 13.2513C10.9985 12.2874 10.2132 11.5022 9.24935 11.5022ZM13.002 13.2513C13.002 14.2152 13.7872 15.0005 14.7511 15.0005C15.715 15.0005 16.5002 14.2152 16.5002 13.2513C16.5002 12.2875 15.7149 11.5022 14.7511 11.5022C13.7873 11.5022 13.002 12.2875 13.002 13.2513Z"
          fill="white"
          stroke="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_10965_7617">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  discord: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <g clipPath="url(#clip0_10965_11128)">
        <path
          d="M0 12C0 18.6274 5.37258 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12Z"
          fill="#596AEA"
        />
        <path
          d="M17.9049 7.38929C17.9049 7.38929 16.7586 6.17565 14.1557 6V6.38321C14.1557 6.38321 15.5833 6.30028 17.3505 8.0398C16.0166 7.08012 14.1137 6.47895 12 6.47895C9.88633 6.47895 7.98342 7.08004 6.64951 8.0398C8.41668 6.30028 9.84428 6.38321 9.84428 6.38321V6C7.24144 6.17565 6.09509 7.38929 6.09509 7.38929C4.11174 9.94404 4 15.5329 4 15.5329C5.86824 17.7844 8.21561 17.4971 8.21561 17.4971L9.07786 16.2516C7.96591 15.9508 7.17184 15.2602 6.69458 14.7224C8.02609 15.6635 9.90989 16.2516 12 16.2516C14.0901 16.2516 15.9739 15.6636 17.3054 14.7224C16.8282 15.2601 16.0341 15.9508 14.9221 16.2516L15.7844 17.4971C15.7844 17.4971 18.1318 17.7846 20 15.5329C20.0002 15.5329 19.8884 9.94404 17.9049 7.38929ZM9.50907 13.9521C8.75945 13.9521 8.15178 13.3065 8.15178 12.5099C8.15178 11.7134 8.75945 11.0677 9.50907 11.0677C10.2587 11.0677 10.8664 11.7134 10.8664 12.5099C10.8664 13.3064 10.2587 13.9521 9.50907 13.9521ZM14.4911 13.9521C13.7415 13.9521 13.1338 13.3065 13.1338 12.5099C13.1338 11.7134 13.7415 11.0677 14.4911 11.0677C15.2407 11.0677 15.8484 11.7134 15.8484 12.5099C15.8484 13.3064 15.2408 13.9521 14.4911 13.9521Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_10965_11128">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  medium: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
        fill="#121214"
      />
      <path
        d="M12.7868 11.9862C12.7868 15.126 10.2596 17.6655 7.14962 17.6655C4.03955 17.6655 1.51221 15.126 1.51221 11.9862C1.51221 8.84633 4.03938 6.30664 7.14962 6.30664C10.2598 6.30664 12.7868 8.8463 12.7868 11.9862Z"
        fill="white"
        stroke="white"
      />
      <mask id="path-3-inside-1_11691_19257" fill="white">
        <path d="M20.0197 11.9875C20.0197 15.1998 18.6458 17.805 16.951 17.805C15.2562 17.805 13.8823 15.1998 13.8823 11.9875C13.8823 8.7751 15.256 6.16992 16.9508 6.16992C18.6456 6.16992 20.0195 8.77427 20.0195 11.9875" />
      </mask>
      <path
        d="M20.0197 11.9875C20.0197 15.1998 18.6458 17.805 16.951 17.805C15.2562 17.805 13.8823 15.1998 13.8823 11.9875C13.8823 8.7751 15.256 6.16992 16.9508 6.16992C18.6456 6.16992 20.0195 8.77427 20.0195 11.9875"
        fill="white"
      />
      <path
        d="M19.0197 11.9875C19.0197 13.4644 18.7017 14.7521 18.2364 15.6343C17.747 16.5622 17.2435 16.805 16.951 16.805V18.805C18.3533 18.805 19.3842 17.7452 20.0054 16.5674C20.6508 15.3439 21.0197 13.7229 21.0197 11.9875H19.0197ZM16.951 16.805C16.6585 16.805 16.1551 16.5622 15.6656 15.6343C15.2003 14.7521 14.8823 13.4644 14.8823 11.9875H12.8823C12.8823 13.7229 13.2513 15.3439 13.8966 16.5674C14.5178 17.7452 15.5487 18.805 16.951 18.805V16.805ZM14.8823 11.9875C14.8823 10.5106 15.2003 9.22278 15.6655 8.34056C16.1549 7.41266 16.6584 7.16992 16.9508 7.16992V5.16992C15.5485 5.16992 14.5177 6.22977 13.8965 7.40756C13.2512 8.63104 12.8823 10.252 12.8823 11.9875H14.8823ZM16.9508 7.16992C17.2434 7.16992 17.7469 7.41265 18.2362 8.34032C18.7015 9.22239 19.0195 10.5101 19.0195 11.9875H21.0195C21.0195 10.2516 20.6506 8.6306 20.0052 7.40718C19.3839 6.22937 18.353 5.16992 16.9508 5.16992V7.16992Z"
        fill="white"
        mask="url(#path-3-inside-1_11691_19257)"
      />
      <path
        d="M22.2731 11.9909C22.2731 13.4062 22.154 14.6735 21.9675 15.5744C21.8822 15.9862 21.7871 16.2999 21.6939 16.5107C21.6006 16.2999 21.5056 15.9864 21.4204 15.5746C21.2339 14.6738 21.1147 13.4065 21.1147 11.9909C21.1147 10.5753 21.2339 9.308 21.4204 8.40718C21.5057 7.99554 21.6007 7.68205 21.6939 7.47123C21.7871 7.68204 21.8821 7.99551 21.9673 8.40713C22.1539 9.30791 22.2731 10.5752 22.2731 11.9909Z"
        fill="white"
        stroke="white"
      />
    </svg>
  ),
};
